import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Layout, Menu, Avatar, Typography } from 'antd';
import { FileTextOutlined, UserOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import '../styles/ScholarSidebar.css';

const { Sider } = Layout;
const { Text } = Typography;

/**
 * ScholarSidebar - A reusable sidebar component for Scholar pages
 * 
 * @param {Object} props - Component props
 * @param {string} props.activePage - Current active page ('acknowledgement-receipt', 'profile', 'logout')
 * @returns {JSX.Element} - Sidebar component with navigation (desktop only)
 */
const ScholarSidebar = ({ activePage }) => {
    const history = useHistory();
    // Get initial collapsed state from localStorage or default to false (expanded)
    const [collapsed, setCollapsed] = useState(() => {
        const savedState = localStorage.getItem('sidebar_collapsed');
        return savedState ? JSON.parse(savedState) : false;
    });
    const [userData, setUserData] = useState({
        name: ''
    });

    // Save collapsed state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('sidebar_collapsed', JSON.stringify(collapsed));
    }, [collapsed]);

    // Function to update user data from localStorage
    const updateUserDataFromLocalStorage = () => {
        // Get user data from localStorage
        const first_name = localStorage.getItem('first_name') || '';
        const middle_name = localStorage.getItem('middle_name') || '';
        const last_name = localStorage.getItem('last_name') || '';
        const suffix = localStorage.getItem('suffix') || '';
        
        // Format full name with middle name and suffix
        let fullName = first_name;
        
        if (middle_name) {
            fullName += ` ${middle_name}`;
        }
        
        if (last_name) {
            fullName += ` ${last_name}`;
        }
        
        if (suffix) {
            fullName += ` ${suffix}`;
        }

        setUserData({
            name: fullName.trim()
        });
    };

    useEffect(() => {
        // Initial load of user data
        updateUserDataFromLocalStorage();
        
        // Set up storage event listener to update when localStorage changes
        window.addEventListener('storage', updateUserDataFromLocalStorage);
        
        // Listen for custom userDataChanged event
        window.addEventListener('userDataChanged', updateUserDataFromLocalStorage);
        
        // Cleanup listeners on unmount
        return () => {
            window.removeEventListener('storage', updateUserDataFromLocalStorage);
            window.removeEventListener('userDataChanged', updateUserDataFromLocalStorage);
        };
    }, []);

    const handleNavigation = (path) => {
        // Navigate without collapsing the sidebar
        history.push(path);
    };

    // Menu items configuration with dynamic active state
    const menuItems = [
        {
            key: 'acknowledgement-receipt',
            icon: <FileTextOutlined />,
            label: 'AR',
            className: activePage === 'acknowledgement-receipt' ? 'ss-active' : '',
            onClick: () => handleNavigation('/scholar-acknowledgement-receipt')
        },
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
            className: activePage === 'profile' ? 'ss-active' : '',
            onClick: () => handleNavigation('/scholar-profile')
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            className: activePage === 'logout' ? 'ss-active' : '',
            onClick: () => handleNavigation('/scholar-logout')
        }
    ];

    return (
        <Sider 
            className="ss-sidebar"
            width={250}
            collapsedWidth={80}
            collapsed={collapsed}
            theme="light"
            style={{ border: 'none', borderRight: 'none' }}
            // Prevent auto-collapsing by setting breakpoint to a very low value
            breakpoint="xs"
            // Disable collapsible property to prevent auto-collapse on menu item click
            collapsible={false}
            // Disable built-in toggle functionality completely
            trigger={null}
            // Keep the current state when window resizes
            onBreakpoint={(broken) => {
                // Only respond to breakpoint if manually set
                if (broken) {
                    // Save the current state before mobile view forces collapse
                    localStorage.setItem('sidebar_collapsed_before_mobile', JSON.stringify(collapsed));
                } else {
                    // Restore previous state when returning from mobile view
                    const previousState = localStorage.getItem('sidebar_collapsed_before_mobile');
                    if (previousState !== null) {
                        setCollapsed(JSON.parse(previousState));
                    }
                }
            }}
            // Prevent the Sider's built-in collapse
            onCollapse={() => {}}
        >
            <div className='ss-sidebar-header'>
                <Avatar
                    size={collapsed ? 40 : 64}
                    className='ss-user-avatar'
                    icon={<UserOutlined />}
                    alt="Scholar Profile"
                />
                {!collapsed && (
                    <div className='ss-user-info'>
                        <Text className='ss-user-name'>{userData.name}</Text>
                    </div>
                )}
            </div>
            
            {/* Use Ant Design Menu instead of custom navigation */}
            <Menu
                className='ss-navigation'
                mode="inline"
                selectedKeys={[activePage]}
                items={menuItems}
                inlineCollapsed={collapsed}
                onClick={(info) => {
                    // The onClick handler from the menu item will be called
                    // We don't need to do anything here to prevent collapse
                }}
            />
            
            <div 
                className="ss-sidebar-trigger" 
                onClick={() => setCollapsed(!collapsed)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
                {collapsed ? (
                    <MenuUnfoldOutlined />
                ) : (
                    <MenuFoldOutlined />
                )}
            </div>
        </Sider>
    );
};

ScholarSidebar.propTypes = {
    activePage: PropTypes.string.isRequired
};

export default ScholarSidebar;